import React, { useEffect, useRef } from 'react';
import cytoscape, { Core, ElementDefinition } from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { Box, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CropFreeIcon from '@mui/icons-material/CropFree';

import { edgeLabel } from '../style/cyStyle';
import { baseStyle } from 'components/Common/cytoscapeGraphStyle';

cytoscape.use(dagre);

export default function GraphView({
  elements,
  edgeLabels,
  onSelectNode,
  onBackgroundTap,
  viewportNonce,
}: Readonly<{
  elements: ElementDefinition[];
  edgeLabels: boolean;
  onSelectNode: (data: any) => void;
  onBackgroundTap: () => void;
  viewportNonce: number;
}>) {
  const hostRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);

  const handleZoomIn = () => {
    const cy = cyRef.current;
    if (cy) {
      const currentZoom = cy.zoom();
      cy.zoom(Math.min(currentZoom + 0.15, cy.maxZoom()));
    }
  };

  const handleZoomOut = () => {
    const cy = cyRef.current;
    if (cy) {
      const currentZoom = cy.zoom();
      cy.zoom(Math.max(currentZoom - 0.15, cy.minZoom()));
    }
  };

  const handleFit = () => {
    const cy = cyRef.current;
    if (cy) {
      cy.fit(undefined, 36);
    }
  };

  useEffect(() => {
    if (!hostRef.current) return;
    if (cyRef.current) {
      try {
        cyRef.current.destroy();
      } catch {}
      cyRef.current = null;
    }
    const cy = cytoscape({
      container: hostRef.current,
      elements,
      style: [
        ...baseStyle,
        {
          selector: '.dull',
          style: {
            'opacity': 0.55,
          }
        },
        {
          selector: '.highlighted',
          style: {
            'opacity': 1,
          }
        },
        {
          selector: 'node.highlighted',
          style: {
            'border-width': 2,
            'border-color': '#39ACE7',
          }
        }
      ],
      layout: {
        name: 'dagre',
        nodeSep: 60,
        rankSep: 160,
        edgeSep: 24,
        rankDir: 'LR',
        padding: 40,
        animate: false,
      } as any,
      wheelSensitivity: 0.25,
      minZoom: 0.1,
      maxZoom: 3.5,
      textureOnViewport: true,
      hideEdgesOnViewport: true,
      pixelRatio: 1,
    });
    cyRef.current = cy;
    cy.fit(undefined, 36);
    cy.on('select', 'node', (evt: cytoscape.EventObject) => {
      const selectedNode = evt.target;
      onSelectNode(selectedNode.data());

      cy.batch(() => {
        cy.elements().addClass('dull').removeClass('highlighted');
        selectedNode.removeClass('dull').addClass('highlighted');
      });
    });

    cy.on('tap', (evt: cytoscape.EventObject) => {
      if (evt.target === cy) {
        cy.$(':selected').unselect();
        cy.batch(() => {
          cy.elements().removeClass('dull highlighted');
        });
        onBackgroundTap();
      }
    });
    return () => {
      try {
        cy.destroy();
      } catch {}
      cyRef.current = null;
    };
  }, [elements]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const obs = new ResizeObserver(() => {
      const cy = cyRef.current;
      if (cy) {
        cy.resize();
        cy.fit(undefined, 36);
      }
    });
    obs.observe(host);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onWin = () => {
      const cy = cyRef.current;
      if (cy) {
        cy.resize();
        cy.fit(undefined, 36);
      }
    };
    window.addEventListener('resize', onWin);
    return () => window.removeEventListener('resize', onWin);
  }, []);

  useEffect(() => {
    const handler = (ev: any) => {
      if (!ev?.propertyName) return;
      if (ev.propertyName.includes('width')) {
        const cy = cyRef.current;
        if (cy) {
          cy.resize();
          cy.fit(undefined, 36);
        }
      }
    };
    document.addEventListener('transitionend', handler);
    return () => document.removeEventListener('transitionend', handler);
  }, []);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() =>
      cy
        .style()
        .selector('edge')
        .style(
          'label',
          (edgeLabels
            ? (el: any) => edgeLabel(el.data('relation'), el.data('context'))
            : '') as any,
        )
        .update(),
    );
  }, [edgeLabels]);

  useEffect(() => {
    const cy = cyRef.current;
    if (cy) {
      cy.resize();
      cy.fit(undefined, 36);
    }
  }, [viewportNonce]);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          right: 24,
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          backgroundColor: '#FFFFFF',
          padding: '8px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E7EB',
        }}
      >
        <Tooltip title="Zoom In" placement="left" arrow>
          <IconButton
            onClick={handleZoomIn}
            size="small"
            sx={{
              color: '#003771',
              backgroundColor: '#F3F4F6',
              '&:hover': { backgroundColor: '#E5E7EB', color: '#39ACE7' },
              width: 32,
              height: 32,
            }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out" placement="left" arrow>
          <IconButton
            onClick={handleZoomOut}
            size="small"
            sx={{
              color: '#003771',
              backgroundColor: '#F3F4F6',
              '&:hover': { backgroundColor: '#E5E7EB', color: '#39ACE7' },
              width: 32,
              height: 32,
            }}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit to Screen" placement="left" arrow>
          <IconButton
            onClick={handleFit}
            size="small"
            sx={{
              color: '#003771',
              backgroundColor: '#F3F4F6',
              '&:hover': { backgroundColor: '#E5E7EB', color: '#39ACE7' },
              width: 32,
              height: 32,
            }}
          >
            <CropFreeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
